import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardHat, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Wachtwoord moet minstens 6 tekens bevatten.");
      return;
    }
    if (password !== confirm) {
      toast.error("De wachtwoorden komen niet overeen.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Je wachtwoord is bijgewerkt. Log nu in.");
      navigate("/inloggen");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 bg-orange-500 flex items-center justify-center rounded-sm">
            <HardHat className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-extrabold">Work4you</span>
        </Link>

        {!token ? (
          <div data-testid="reset-invalid-message" className="bg-white border border-slate-200 rounded-md p-8 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h1 className="font-display text-2xl font-extrabold text-slate-950 mb-2">Ongeldige link</h1>
            <p className="text-sm text-slate-600">Deze herstel-link is ongeldig of onvolledig. Vraag een nieuwe aan.</p>
            <Link to="/wachtwoord-vergeten" data-testid="request-new-link" className="inline-block mt-6 text-orange-600 font-semibold hover:underline text-sm">
              Nieuwe link aanvragen
            </Link>
          </div>
        ) : (
          <>
            <p className="label-caps mb-2">Nieuw wachtwoord</p>
            <h1 className="font-display text-3xl font-extrabold text-slate-950 mb-8">Stel een nieuw wachtwoord in</h1>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs font-bold text-slate-700">Nieuw wachtwoord</Label>
                <Input data-testid="reset-password-input" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-sm border-slate-300" placeholder="Minimaal 6 tekens" />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700">Bevestig wachtwoord</Label>
                <Input data-testid="reset-confirm-input" type="password" required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="rounded-sm border-slate-300" placeholder="Herhaal je wachtwoord" />
              </div>
              <Button data-testid="reset-submit-btn" type="submit" disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold h-11">
                {loading ? "Bezig..." : "Wachtwoord opslaan"}
              </Button>
            </form>
            <p className="text-center text-sm text-slate-500 mt-6">
              <Link to="/inloggen" data-testid="back-to-login-link" className="text-orange-600 font-semibold hover:underline">
                Terug naar inloggen
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
